import * as THREE from 'three';
import type { BattleState, MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { stoneBoar } from '../models/boar.ts';
import type { Model } from '../models/types.ts';
import type { OmenCtx } from '../omens/common.ts';
import { simToWorld, WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { frameSpot } from './orderpost.ts';

/**
 * The Order's great bell (El Párpado 2, docs/SAGAS.md). On the climb it is the escort's caravan: lying on its side,
 * crown uphill, mouth down the slope, dragged by four ropes from the yoke's lugs. A rope whole runs taut to a hero
 * pulling beside it (or to one of Ulla's stone boars in front, if they came); a rope with no one on it lies slack in
 * the dirt; a cut one lies short and frayed. A slip throws dust off its lip; brought down, it rolls away down the
 * slope. Then hanging it: upright under the frame by the node, rising into it as the winches wind; three winches round
 * the frame (a drum, its spokes turning while a hero stands at it, a rope from it up to the beam) and a ring on the
 * ground at each where to stand.
 *
 * The bell is old bronze gone green: patina, darker bands, the worn brown of the metal at its lip, and the crack down
 * its side. No gold. Draw calls: the bell (one merged mesh), the ropes (one instanced), the boars (two small models),
 * the winches' bases (one), their spokes (one instanced), the rings (one instanced). No lights.
 */

const BELL_H = 1.55;
/** Its size on the board (the frame was built for it: a hero comes up to its shoulder). */
const K = 0.82;
/** The bell's profile (radius, height) from its mouth up to its crown. */
const PROFILE: [number, number][] = [
  [0.8, 0],
  [0.83, 0.05],
  [0.76, 0.13],
  [0.66, 0.32],
  [0.58, 0.6],
  [0.55, 0.95],
  [0.53, 1.2],
  [0.46, 1.38],
  [0.3, 1.5],
  [0, BELL_H],
];
const PATINA = '#5b8d78';
const PATINA_DARK = '#33544a';
const BRONZE = '#5c4636';
const CRACK = '#121110';
const ROPE = '#6e6150';
const IRON = '#2a2a2e';
const WOOD = ['#3e3024', '#4a3a2c', '#33281e'];
const RING_IDLE = new THREE.Color('#ff8a6e');
const RING_ON = new THREE.Color('#f4f5f7');
const DUST = '#9a947e';
/** How far above the ground the lying bell's axis is (its waist's radius). */
const LIE = 0.66;
/** Lying, it rests on its lip and its shoulder: the crown dips this much. */
const DIP = 0.26;
/** Ropes: the climb's four, then a winch's three. */
const ROPES = 7;
const ROPE_R = 0.035;

const S = WORLD_PER_HEX;

type TollRules = NonNullable<NonNullable<BattleState['rules']>['toll']>;
/** The bell at midnight: the beat's target ring round the node and where the closing ring starts (world). */
const TARGET = 1.0;
const FAR = 4.6;
const BEAT = new THREE.Color('#ff8a6e');
const BEAT_HOT = new THREE.Color('#f4f5f7');
const CALM = new THREE.Color('#9fc8ff');
const CALM_CORE = new THREE.Color('#e8f0ff');

export class BellLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  /** The bell, upright in its own space (mouth at 0, crown up), in a holder that lays it down or hangs it. */
  private bell = new THREE.Group();
  private bellMesh: THREE.Mesh;
  private ropes: THREE.InstancedMesh;
  private boars: Model[] = [];
  private spokes: THREE.InstancedMesh | null = null;
  private rings: THREE.InstancedMesh | null = null;
  private winches: THREE.Vector3[] = [];
  private turn: number[] = [];
  private spot: { x: number; z: number; a: number; H: number } | null = null;
  /** Where the bell is drawn this frame (from the caravan's drawn place), and the way up the slope there. */
  private at = new THREE.Vector3();
  private placed = false;
  private yaw = 0;
  private yawSet = false;
  private moving = false;
  private lift = 0;
  private roll: { t: number; x: number; z: number; dx: number; dz: number } | null = null;
  private now = 0;
  private last = 0;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private w = new THREE.Vector3();
  private up = new THREE.Vector3(0, 1, 0);
  private one = new THREE.Vector3(1, 1, 1);
  private sc = new THREE.Vector3();
  private lugs = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];

  /** The bell at midnight: its beat's rings, the reach, its glow; how far it swings (angle, speed), a blow's flash. */
  private beat: { target: THREE.Mesh; closing: THREE.Mesh; reach: THREE.Mesh; targetMat: THREE.MeshBasicMaterial; closingMat: THREE.MeshBasicMaterial; glow: GlowLayer } | null = null;
  private swing = 0;
  private swingV = 0;
  private struck = 0;
  private calmK = 0;

  constructor(
    private ctx: OmenCtx,
    map: MapDef,
    private mode: 'climb' | 'hang' | 'toll',
    boars: number,
    winches: { x: number; y: number }[] = [],
    private toll?: TollRules,
  ) {
    this.group.name = 'order-bell';
    // ---- The bell: one merged shell, its bands, the yoke's lugs on the crown, the crack.
    const kit = new Kit();
    const pts = PROFILE.map(([r, y]) => new THREE.Vector2(r, y));
    const shell = new THREE.LatheGeometry(pts, 22);
    kit.add(shell, PATINA, new THREE.Matrix4());
    // The inside, dark, a hair smaller (seen down the mouth as it lies).
    const inner = new THREE.LatheGeometry(PROFILE.slice(0, -1).map(([r, y]) => new THREE.Vector2(Math.max(0.01, r - 0.06), Math.min(BELL_H - 0.12, y + 0.02))), 22);
    kit.add(inner, '#1d2a25', new THREE.Matrix4());
    // The worn lip: the bronze shows brown under the green.
    const lip = new THREE.TorusGeometry(0.8, 0.045, 5, 22).rotateX(Math.PI / 2);
    kit.add(lip, BRONZE, place(0, 0.05, 0));
    // Bands round the shoulder and the waist.
    for (const [y, r] of [
      [0.24, 0.71],
      [1.22, 0.53],
      [1.3, 0.51],
    ] as [number, number][]) {
      kit.add(new THREE.TorusGeometry(r, 0.03, 4, 22).rotateX(Math.PI / 2), PATINA_DARK, place(0, y, 0));
    }
    // A row of the Order's eyes cast round the waist (little closed lids), worn.
    const eye = new THREE.SphereGeometry(0.06, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2).scale(1.6, 0.5, 1);
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * Math.PI * 2;
      kit.add(eye, PATINA_DARK, place(Math.cos(a) * 0.565, 0.82, Math.sin(a) * 0.565, -a + Math.PI / 2, 1, Math.PI / 2));
    }
    // The yoke's lugs on the crown: an arched handle each way, iron-dark bronze.
    for (let k = 0; k < 2; k++) kit.add(new THREE.TorusGeometry(0.16, 0.05, 5, 10, Math.PI), PATINA_DARK, place(0, BELL_H - 0.02, 0, (k * Math.PI) / 2));
    // The crack: from the lip up its side to the waist, in a few jagged runs.
    const crack: [number, number, number][] = [
      [0.06, 0.09, 0.0],
      [0.25, 0.22, 0.06],
      [0.45, 0.19, -0.05],
      [0.7, 0.24, 0.04],
      [0.92, 0.12, 0.0],
    ];
    for (const [y, len, tilt] of crack) {
      const r = PROFILE.reduce((best, [pr, py]) => (Math.abs(py - y) < Math.abs(best[1] - y) ? [pr, py] : best), PROFILE[0] as [number, number])[0];
      kit.add(new THREE.BoxGeometry(0.03, len, 0.03), CRACK, place(r + 0.005, y + len / 2, 0.02, 0, 1, 0, tilt));
    }
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.55, metalness: 0.35, side: THREE.DoubleSide });
    this.mats.push(mat);
    const mesh = kit.build(mat, 'order-bell');
    this.bellMesh = mesh ?? new THREE.Mesh();
    this.bellMesh.castShadow = true;
    this.geos.push(this.bellMesh.geometry);
    this.bell.add(this.bellMesh);
    this.group.add(this.bell);
    for (const g of [shell, inner, lip, eye]) g.dispose();

    // ---- The ropes: a unit cylinder along +y, stretched between two points each frame.
    const rope = new THREE.CylinderGeometry(ROPE_R, ROPE_R, 1, 5).translate(0, 0.5, 0);
    this.geos.push(rope);
    const rm = new THREE.MeshStandardMaterial({ color: ROPE, roughness: 1 });
    this.mats.push(rm);
    this.ropes = new THREE.InstancedMesh(rope, rm, ROPES);
    this.ropes.name = 'bell-ropes';
    this.ropes.count = 0;
    this.ropes.frustumCulled = false;
    this.group.add(this.ropes);

    // ---- Ulla's stone boars, harnessed in front.
    for (let k = 0; k < Math.min(2, boars); k++) {
      const b = stoneBoar({ seed: 3 + k });
      b.root.scale.setScalar(1.15);
      this.group.add(b.root);
      this.boars.push(b);
    }

    if (mode === 'hang') {
      this.spot = frameSpot(map, 'post', winches);
      this.buildWinches(winches);
    }
    if (mode === 'toll') {
      this.spot = frameSpot(map, 'post', []);
      this.lift = 1;
      this.buildBeat();
    }
  }

  /**
   * The bell at midnight (El Párpado 4): round the node a faint circle of where the hammer reaches, and the beat as on
   * Las vías' Anvil: a ring of light closing over the ground onto a target ring; the instant they meet it flashes white.
   */
  private buildBeat(): void {
    const g0 = this.ctx.ground(0, 0);
    const ringGeo = (w: number): THREE.BufferGeometry => new THREE.RingGeometry(1 - w, 1, 64).rotateX(-Math.PI / 2);
    const mat = (o: number): THREE.MeshBasicMaterial => new THREE.MeshBasicMaterial({ color: BEAT, transparent: true, opacity: o, depthWrite: false, blending: THREE.AdditiveBlending });
    const tg = ringGeo(0.14);
    const cg = ringGeo(0.05);
    const rg = ringGeo(0.025);
    this.geos.push(tg, cg, rg);
    const targetMat = mat(0.4);
    const closingMat = mat(0.5);
    const reachMat = mat(0.18);
    this.mats.push(targetMat, closingMat, reachMat);
    const target = new THREE.Mesh(tg, targetMat);
    const closing = new THREE.Mesh(cg, closingMat);
    const reach = new THREE.Mesh(rg, reachMat);
    for (const m of [target, closing, reach]) {
      m.position.set(0, g0 + 0.06, 0);
      m.renderOrder = 2;
      m.frustumCulled = false;
    }
    target.scale.setScalar(TARGET);
    reach.scale.setScalar(((this.toll?.reach ?? 1900) / 1000) * S);
    closing.visible = false;
    const glow = new GlowLayer(24);
    this.group.add(target, closing, reach, glow.mesh);
    this.beat = { target, closing, reach, targetMat, closingMat, glow };
  }

  /** Hanging it: the three winches (a stone foot, a drum on a post, its spokes) and a ring at each where to stand. */
  private buildWinches(at: { x: number; y: number }[]): void {
    const kit = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    for (const [i, p] of at.entries()) {
      const w = simToWorld(p.x, p.y, new THREE.Vector3(), 0);
      w.y = this.ctx.ground(w.x, w.z);
      this.winches.push(w);
      this.turn.push(0);
      const g = w.y;
      // A ring of stones it is pinned in, a squat timber foot, the drum wound with rope, an iron cap.
      for (let k = 0; k < 6; k++) {
        const t = (k / 6) * Math.PI * 2 + i;
        kit.add(rock, ['#6e6c68', '#5e5c58', '#7a7772'][k % 3] as string, place(w.x + Math.cos(t) * 0.42, g + 0.06, w.z + Math.sin(t) * 0.42, k, 0.2));
      }
      kit.add(box, WOOD[2] as string, place(w.x, g + 0.08, w.z, i, new THREE.Vector3(0.6, 0.16, 0.6)));
      kit.add(cyl, WOOD[0] as string, place(w.x, g + 0.45, w.z, 0, new THREE.Vector3(0.36, 0.6, 0.36)));
      for (let k = 0; k < 4; k++) kit.add(new THREE.TorusGeometry(0.19, 0.03, 4, 10).rotateX(Math.PI / 2), ROPE, place(w.x, g + 0.28 + k * 0.08, w.z));
      kit.add(cyl, IRON, place(w.x, g + 0.79, w.z, 0, new THREE.Vector3(0.22, 0.08, 0.22)));
    }
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, flatShading: true });
    this.mats.push(mat);
    const base = kit.build(mat, 'winch-bases');
    if (base) {
      base.castShadow = true;
      this.geos.push(base.geometry);
      this.group.add(base);
    }
    // The spokes: a cross of two bars through the drum's head, turned while a hero stands at it.
    const sk = new Kit();
    sk.add(box, WOOD[1] as string, place(0, 0, 0, 0, new THREE.Vector3(1.3, 0.07, 0.07)));
    sk.add(box, WOOD[1] as string, place(0, 0, 0, Math.PI / 2, new THREE.Vector3(1.3, 0.07, 0.07)));
    for (const [x, z] of [
      [0.62, 0],
      [-0.62, 0],
      [0, 0.62],
      [0, -0.62],
    ]) sk.add(box, IRON, place(x as number, 0, z as number, 0, 0.09));
    const sm = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true });
    this.mats.push(sm);
    const cross = sk.build(sm, 'winch-spokes');
    if (cross) {
      this.geos.push(cross.geometry);
      this.spokes = new THREE.InstancedMesh(cross.geometry, sm, Math.max(1, at.length));
      this.spokes.name = 'winch-spokes';
      this.spokes.castShadow = true;
      this.group.add(this.spokes);
    }
    // Where to stand: a ring on the ground round each (the reach), pale red idle, white while it turns.
    const ring = new THREE.RingGeometry(0.88, 1.0, 40).rotateX(-Math.PI / 2);
    this.geos.push(ring);
    const rm = new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.55, depthWrite: false });
    this.mats.push(rm);
    this.rings = new THREE.InstancedMesh(ring, rm, Math.max(1, at.length));
    this.rings.name = 'winch-rings';
    this.rings.renderOrder = 2;
    for (const [i, w] of this.winches.entries()) {
      this.m.makeTranslation(w.x, w.y + 0.04, w.z);
      this.rings.setMatrixAt(i, this.m);
      this.rings.setColorAt(i, RING_IDLE);
    }
    this.group.add(this.rings);
    for (const g of [box, cyl, rock]) g.dispose();
  }

  /** Where the caravan (the bell) is drawn this frame, in sim units (from the view's interpolation). */
  place(x: number, y: number): void {
    simToWorld(x, y, this.at, 0);
    this.at.y = this.ctx.ground(this.at.x, this.at.z);
    this.placed = true;
  }

  tick(s: BattleState): void {
    const low = this.ctx.low;
    for (const e of s._events ?? []) {
      if (e.t !== 'hoist') continue;
      const p = this.mode === 'climb' && this.placed ? this.v.copy(this.at) : this.ctx.at(e.x, e.y, 0);
      if (e.kind === 'cut' || e.kind === 'slip') {
        // Dust off its lip as it slides back; a cut rope snaps with a spark of fibres.
        this.ctx.shake(e.kind === 'slip' ? 0.25 : 0.15);
        this.ctx.vfx.burst(p.setY(p.y + 0.1), { n: low ? 6 : 16, color: DUST, speed: 0.05, up: 0.03, size: 0.55, life: 30, smoke: true, spread: 1 });
        if (e.kind === 'cut') this.ctx.flare(this.w.copy(p).setY(p.y + LIE), '#ff8a6e', 1.6, 10);
        this.ctx.vfx.ring(p, 0.3, 1.8, DUST, 14, 0.6);
      } else if (e.kind === 'brace') {
        this.ctx.vfx.ring(p.setY(p.y + 0.05), 0.3, 1.6, '#cfe3ff', 14, 0.7);
      } else if (e.kind === 'roll' && this.mode === 'climb' && !this.roll) {
        const dx = -Math.sin(this.yaw);
        const dz = -Math.cos(this.yaw);
        this.roll = { t: this.now, x: this.at.x, z: this.at.z, dx, dz };
        this.ctx.shake(0.4);
      } else if (e.kind === 'up') {
        const top = this.spot ? this.w.set(this.spot.x, this.ctx.ground(this.spot.x, this.spot.z) + this.spot.H - 1, this.spot.z) : p;
        this.ctx.flare(top, '#cfe3ff', 4, 26);
        this.ctx.vfx.ring(this.v.copy(top).setY(top.y - this.spot!.H + 1.1), 0.4, 3.4, '#ff8a6e', 20, 0.7);
      }
    }
    if (this.mode === 'toll' && this.spot) this.tickToll(s);
    // Dragged: a little dust from under it as it goes.
    if (this.mode === 'climb' && this.moving && this.placed && s.tick % (low ? 6 : 3) === 0) {
      this.v.set(this.at.x - Math.sin(this.yaw) * 0.7, this.at.y + 0.05, this.at.z - Math.cos(this.yaw) * 0.7);
      this.ctx.vfx.burst(this.v, { n: 1, color: '#7d7766', speed: 0.01, up: 0.012, size: 0.4, life: 16, smoke: true });
    }
  }

  draw(s: BattleState): void {
    if (this.mode === 'climb') this.drawClimb(s);
    else if (this.mode === 'toll') this.drawToll(s);
    else this.drawHang(s);
  }

  /** The bell at midnight's moments: a blow on the beat, one off it, the calm full. */
  private tickToll(s: BattleState): void {
    const f = this.spot;
    if (!f) return;
    const low = this.ctx.low;
    const g = this.ctx.ground(f.x, f.z);
    const mouth = this.w.set(f.x, g + f.H - 0.5 - BELL_H * K, f.z);
    const node = this.v.set(0, this.ctx.ground(0, 0) + 0.08, 0);
    const rad = ((this.toll?.radius ?? 6000) / 1000) * S;
    for (const e of s._events ?? []) {
      if (e.t !== 'toll') continue;
      if (e.kind === 'hit' || e.kind === 'calm') {
        // The blow: it swings, a cold flash at its mouth, and the sound goes out over the head as rings pushing out.
        this.swingV += e.kind === 'calm' ? 0.12 : 0.08;
        this.struck = 1;
        this.ctx.flare(mouth, '#e8f0ff', e.kind === 'calm' ? 4.4 : 3, e.kind === 'calm' ? 26 : 14);
        this.ctx.vfx.ring(node, TARGET, rad, '#9fc8ff', 18, 0.7);
        this.ctx.vfx.ring(node, TARGET * 0.8, rad * 0.7, '#f4f5f7', 13, 0.5);
        if (!low) this.ctx.vfx.ring(node, TARGET, rad * 0.45, '#ff8a6e', 22, 0.35);
        this.ctx.vfx.burst(mouth, { n: low ? 6 : 14, color: '#cfe3ff', speed: 0.03, up: -0.01, size: 0.05, life: 22, gravity: 0.002, spread: 0.6 });
        this.ctx.shake(e.kind === 'calm' ? 0.3 : 0.12);
        if (e.kind === 'calm') for (const [r0, r1, col, life] of [[TARGET, rad * 1.6, '#e8f0ff', 34], [TARGET, rad * 1.2, '#9fc8ff', 28]] as const) this.ctx.vfx.ring(node, r0, r1, col, life, 0.55);
      } else if (e.kind === 'miss') {
        // Off the beat: a dull clank, the bell barely moving, grey dust off the beam.
        this.swingV += 0.025;
        const p = this.ctx.at(e.x, e.y, 0.6);
        this.ctx.vfx.burst(p, { n: low ? 4 : 9, color: '#8a8e96', speed: 0.04, up: 0.03, size: 0.03, life: 10, gravity: 0.012, spread: 0.2 });
        this.ctx.vfx.burst(this.v.set(f.x, g + f.H, f.z), { n: low ? 3 : 6, color: '#7d7766', speed: 0.01, up: -0.01, size: 0.3, life: 22, smoke: true, spread: 0.6 });
      }
    }
  }

  /** The bell at midnight: hung, swinging after each blow; the beat's rings; its glow growing with the calm. */
  private drawToll(s: BattleState): void {
    const f = this.spot;
    const b = this.beat;
    const r = this.toll;
    if (!f || !b || !r) return;
    const dt = Math.min(0.1, Math.max(0, this.now - this.last));
    this.last = this.now;
    // A heavy pendulum: slow, damped.
    this.swingV += -this.swing * dt * 6;
    this.swingV *= Math.max(0, 1 - dt * 0.9);
    this.swing += this.swingV * dt * 12;
    this.struck = Math.max(0, this.struck - dt * 2.2);
    const st = s.toll;
    const want = st ? Math.min(1, st.calm / Math.max(1, r.need)) : 0;
    this.calmK += (want - this.calmK) * Math.min(1, dt * 2);
    const g = this.ctx.ground(f.x, f.z);
    const top = f.H - 0.5 - BELL_H * K;
    this.bell.position.set(f.x, g + 0.02 + top, f.z);
    this.bell.rotation.set(this.swing, -f.a, 0, 'YXZ');
    this.bell.scale.setScalar(K);
    this.bell.updateMatrixWorld(true);
    // The beat: the ring closes at an even pace onto the target; in the window, the target flashes white.
    const running = !!st && s.started && !st.done && s.result === 'running';
    const t = s.waveT;
    let flash = 0;
    if (running && st && t >= st.open) {
      const k = Math.min(1, Math.max(0, (t - st.open) / Math.max(1, st.close - st.open)));
      b.closing.visible = t <= st.close + r.window;
      b.closing.scale.setScalar(Math.max(TARGET, FAR + (TARGET - FAR) * k));
      b.closingMat.opacity = 0.25 + 0.6 * k;
      b.closingMat.color.copy(BEAT).lerp(BEAT_HOT, k * k);
      flash = Math.abs(t - st.close) <= r.window ? 1 - Math.abs(t - st.close) / (r.window + 1) : 0;
    } else b.closing.visible = false;
    const pulse = running ? 0.35 + 0.1 * Math.sin(this.now * 3) : 0.12;
    b.targetMat.opacity = Math.min(1, pulse + flash * 0.65 + this.struck * 0.5);
    b.targetMat.color.copy(BEAT).lerp(BEAT_HOT, Math.max(flash, this.struck)).lerp(CALM, this.calmK * 0.5);
    b.target.scale.setScalar(TARGET * (1 + flash * 0.06 + this.struck * 0.08));
    b.target.visible = running || this.struck > 0.01;
    b.reach.visible = running;
    // The calm: a cold light gathering under the bell and over the node, stronger with every blow on the beat.
    const mouthY = g + top + 0.3;
    b.glow.begin();
    b.glow.add(f.x, mouthY, f.z, CALM, 1.4 + this.calmK * 2.4, 0.08 + 0.22 * this.calmK + this.struck * 0.4);
    b.glow.add(f.x, mouthY, f.z, CALM_CORE, 0.5, 0.1 + 0.2 * this.calmK + this.struck * 0.5);
    b.glow.add(0, this.ctx.ground(0, 0) + 0.1, 0, CALM, 3 + this.calmK * 4, 0.04 + 0.08 * this.calmK);
    if (flash > 0) b.glow.add(0, this.ctx.ground(0, 0) + 0.12, 0, BEAT_HOT, TARGET * 2.6, 0.3 * flash);
    b.glow.end();
  }

  private drawClimb(s: BattleState): void {
    const e = s.escort;
    const r = s.rules?.climb;
    if (!e || !r || !this.placed) return;
    // Uphill: along the road ahead of where it is (or of where it slides back to).
    const sliding = (s.climb?.to ?? -1) >= 0;
    const i = Math.min(e.road.length - 1, Math.max(1, sliding ? (s.climb?.to ?? 0) + 1 : e.next));
    const a = e.road[i - 1];
    const b = e.road[i];
    if (a && b && (a.x !== b.x || a.y !== b.y)) {
      // Sim (x, y) → world (x, z): yaw so its local +z (the crown) points uphill.
      const want = Math.atan2(b.x - a.x, b.y - a.y);
      if (!this.yawSet) this.yaw = want;
      else this.yaw += Math.atan2(Math.sin(want - this.yaw), Math.cos(want - this.yaw)) * 0.15;
      this.yawSet = true;
    }
    this.moving = !e.arrived && e.speed > 0;
    let x = this.at.x;
    let z = this.at.z;
    let y = this.at.y;
    let spin = 0;
    let sink = 1;
    if (this.roll) {
      // Brought down: it rolls away down the slope, turning over and over, and is gone into the dust.
      const t = Math.min(2.6, this.now - this.roll.t);
      const d = t * t * 1.6;
      x = this.roll.x + this.roll.dx * d;
      z = this.roll.z + this.roll.dz * d;
      y = this.ctx.ground(x, z) - Math.max(0, t - 1.6) * 0.8;
      spin = t * 4;
      sink = Math.max(0, 1 - Math.max(0, t - 1.8) / 0.8);
    }
    // Lying on its side, crown uphill; rocking a hair as it is dragged, shuddering on a slide.
    const rock = this.moving ? Math.sin(this.now * 6) * 0.04 : sliding ? Math.sin(this.now * 30) * 0.05 : 0;
    this.bell.position.set(x, y + LIE * K * sink, z);
    this.bell.rotation.set(Math.PI / 2 + DIP, this.yaw, 0, 'YXZ');
    // Its mouth back down the slope: the shell moved so the bell's middle sits on the spot, turning on its own axis.
    this.bellMesh.position.set(0, -BELL_H * 0.45, 0);
    this.bellMesh.rotation.set(0, rock + spin, 0);
    this.bell.scale.setScalar(sink * K);
    this.bell.updateMatrixWorld(true);
    // The lugs on the crown, in the world: where the ropes are tied.
    const fx = Math.sin(this.yaw);
    const fz = Math.cos(this.yaw);
    const sx = Math.cos(this.yaw);
    const sz = -Math.sin(this.yaw);
    for (let k = 0; k < 4; k++) {
      const side = (k - 1.5) * 0.14;
      (this.lugs[k] as THREE.Vector3).set(x + fx * BELL_H * 0.53 * K + sx * side, y + (LIE - 0.12) * K * sink, z + fz * BELL_H * 0.53 * K + sz * side);
    }
    // The boars, in harness ahead of it, plodding while it moves.
    const step = this.moving ? Math.abs(Math.sin(this.now * 5)) * 0.05 : 0;
    this.boars.forEach((bo, k) => {
      const side = (k ? 1 : -1) * 0.55;
      const px = x + fx * 2.4 + sx * side;
      const pz = z + fz * 2.4 + sz * side;
      bo.root.position.set(px, this.ctx.ground(px, pz) + (k ? step : 0.05 - step), pz);
      bo.root.rotation.y = this.yaw;
      bo.root.visible = !this.roll && !e.arrived;
    });
    // The ropes: the boars take the first two, the heroes pulling beside it the rest; whole with no one on them, slack;
    // cut, a short frayed end in the dirt.
    const whole = this.roll ? 0 : (s.climb?.ropes ?? r.ropes);
    const pull = (r.pull / 1000) * S;
    let n = 0;
    let hero = 0;
    const pullers = s.heroes.filter((h) => h.downTicks === 0);
    for (let k = 0; k < 4; k++) {
      const from = this.lugs[k] as THREE.Vector3;
      if (k >= whole) {
        // Cut: a short end trailing out to the side.
        this.w.set(from.x + fx * 0.5 + sx * (k - 1.5) * 0.5, this.ctx.ground(from.x, from.z) + 0.03, from.z + fz * 0.5 + sz * (k - 1.5) * 0.5);
        this.rope(n++, from, this.w);
        continue;
      }
      const boar = this.boars[k];
      if (boar && boar.root.visible) {
        this.w.copy(boar.root.position).setY(boar.root.position.y + 0.45);
        this.rope(n++, from, this.w);
        continue;
      }
      // The next hero standing within reach of it.
      let to: THREE.Vector3 | undefined;
      while (hero < pullers.length && !to) {
        const h = pullers[hero++] as BattleState['heroes'][number];
        const hp = this.ctx.pos(h.id);
        if (hp && Math.hypot(hp.x - x, hp.z - z) <= pull) to = this.w.copy(hp).setY(hp.y + 0.55);
      }
      if (to) this.rope(n++, from, to);
      else {
        // No one on it: it lies slack ahead in the dirt.
        this.w.set(from.x + fx * 1.3 + sx * (k - 1.5) * 0.3, this.ctx.ground(from.x, from.z) + 0.03, from.z + fz * 1.3 + sz * (k - 1.5) * 0.3);
        this.rope(n++, from, this.w);
      }
    }
    this.ropes.count = n;
    this.ropes.instanceMatrix.needsUpdate = true;
  }

  private drawHang(s: BattleState): void {
    const f = this.spot;
    const r = s.rules?.winch;
    if (!f || !r) return;
    // Upright under the beam, rising with the winding (eased: it doesn't jump on a tick).
    const want = Math.min(1, (s.winch?.lift ?? 0) / Math.max(1, r.need));
    this.lift += (want - this.lift) * 0.12;
    const g = this.ctx.ground(f.x, f.z);
    // At the top its crown meets the yoke under the beam.
    const top = f.H - 0.5 - BELL_H * K;
    const sway = this.lift > 0.02 && this.lift < 0.99 ? Math.sin(this.now * 1.7) * 0.03 : 0;
    this.bell.position.set(f.x, g + 0.02 + top * this.lift, f.z);
    this.bell.rotation.set(sway, -f.a, 0, 'YXZ');
    this.bell.scale.setScalar(K);
    this.bellMesh.position.set(0, 0, 0);
    this.bellMesh.rotation.set(0, 0, 0);
    this.bell.updateMatrixWorld(true);
    // A rope from each winch's drum up to the beam above the bell; the spokes turn while a hero stands at it.
    const beam = this.w.set(f.x, g + f.H - 0.05, f.z);
    let n = 0;
    const dt = Math.min(0.1, Math.max(0, this.now - this.last));
    this.last = this.now;
    for (const [i, w] of this.winches.entries()) {
      const on = !!s.winch?.turning[i];
      this.v.set(w.x, w.y + 0.62, w.z);
      this.rope(n++, this.v, beam);
      this.turn[i] = ((this.turn[i] ?? 0) + (on ? dt * 2.2 : 0)) % (Math.PI * 2);
      if (this.spokes) {
        this.q.setFromAxisAngle(this.up, this.turn[i] as number);
        this.m.compose(this.v.set(w.x, w.y + 0.66, w.z), this.q, this.one);
        this.spokes.setMatrixAt(i, this.m);
      }
      if (this.rings) {
        this.rings.setColorAt(i, on ? RING_ON : RING_IDLE);
        // Breathing a little while no one is at it, to say "stand here".
        const k = on ? 1 : 1 + 0.06 * Math.sin(this.now * 3 + i);
        this.m.compose(this.sc.set(w.x, w.y + 0.04, w.z), this.q.identity(), this.v.set(k, 1, k));
        this.rings.setMatrixAt(i, this.m);
      }
    }
    if (this.spokes) this.spokes.instanceMatrix.needsUpdate = true;
    if (this.rings) {
      this.rings.instanceMatrix.needsUpdate = true;
      if (this.rings.instanceColor) this.rings.instanceColor.needsUpdate = true;
    }
    this.ropes.count = n;
    this.ropes.instanceMatrix.needsUpdate = true;
  }

  /** Rope `i` from `a` to `b`. */
  private rope(i: number, a: THREE.Vector3, b: THREE.Vector3): void {
    this.sc.subVectors(b, a);
    const len = this.sc.length();
    if (len < 1e-4) {
      this.m.makeScale(0, 0, 0);
      this.ropes.setMatrixAt(i, this.m);
      return;
    }
    this.q.setFromUnitVectors(this.up, this.sc.divideScalar(len));
    this.m.compose(a, this.q, this.sc.set(1, len, 1));
    this.ropes.setMatrixAt(i, this.m);
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    for (const b of this.boars) b.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
    if (this.beat) {
      this.beat.glow.mesh.geometry.dispose();
      this.beat.glow.material.dispose();
    }
  }
}
